import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';

const IN_CHUNK = 150;
const CACHE_TTL_MS = 5 * 60 * 1000;

let cache = new Map<string, string>();
let cacheAt = 0;

function refreshCacheWindow() {
  const now = Date.now();
  if (now - cacheAt > CACHE_TTL_MS) {
    cache = new Map();
    cacheAt = now;
  }
}

/** Look up customer display names for the given codes only (not the full customers table). */
export async function fetchCustomerNamesByCodes(codes: Iterable<string>): Promise<Map<string, string>> {
  refreshCacheWindow();

  const unique = [
    ...new Set(
      [...codes]
        .map((c) => String(c ?? '').trim())
        .filter(Boolean),
    ),
  ];
  const out = new Map<string, string>();
  if (unique.length === 0) return out;

  const missing: string[] = [];
  for (const code of unique) {
    const hit = cache.get(code);
    if (hit !== undefined) out.set(code, hit);
    else missing.push(code);
  }

  if (missing.length === 0) return out;

  const db = getDbSupabase('dem');
  for (let i = 0; i < missing.length; i += IN_CHUNK) {
    const chunk = missing.slice(i, i + IN_CHUNK);
    const { data, error } = await db.from('customers').select('code, name').in('code', chunk);
    if (error) throw new Error(formatSupabaseError(error));
    for (const row of data ?? []) {
      const r = row as Record<string, unknown>;
      const code = String(r.code ?? '').trim();
      if (!code) continue;
      const name = String(r.name ?? '').trim();
      cache.set(code, name);
      out.set(code, name);
    }
  }

  return out;
}

export function collectCustomerCodesFromRows(rows: Record<string, unknown>[]): string[] {
  return rows.map((row) => String(row.customer_code ?? '').trim()).filter(Boolean);
}

/** Resolve customer codes whose code or name matches a search keyword (for order list cloud search). */
export async function findCustomerCodesMatchingSearch(
  search: string,
  limit = 80,
): Promise<string[]> {
  const kw = search.trim();
  if (!kw) return [];
  const escaped = kw.replace(/[%_]/g, '\\$&');
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('customers')
    .select('code')
    .or(`code.ilike.%${escaped}%,name.ilike.%${escaped}%`)
    .limit(limit);
  if (error) throw new Error(formatSupabaseError(error));
  return [
    ...new Set(
      (data ?? [])
        .map((row) => String((row as Record<string, unknown>).code ?? '').trim())
        .filter(Boolean),
    ),
  ];
}
